import type { Country, Language } from "@commerce-shared/site-config";
import { getLocalizedStrict, languageOf } from "@commerce-shared/utility";
import type {
	LocalizedString,
	ProductVariant,
} from "@commercetools/platform-sdk";

export type PageName = keyof typeof pageTranslations | "content/[...slug]";

export type Breadcrumb = { name: string; path: string };

export const getSitePath = (country: Country, language: Language) => {
	if (language === country.toLowerCase()) return `/${country.toLowerCase()}`;
	if (country === "AT" && language === "de") return "/at";
	return `/${country.toLowerCase()}-${language}`;
};

// TODO: Correct IT and ES translations where needed
export const pageTranslations = {
	search: {
		nl: "zoeken",
		fr: "recherche",
		de: "suche",
		it: "ricerca",
		es: "buscar",
	},
	cart: {
		nl: "winkelwagen",
		fr: "panier",
		de: "warenkorb",
		it: "carrello",
		es: "carrito",
	},
	checkout: {
		nl: "afrekenen",
		fr: "paiement",
		de: "kasse",
		it: "pagamento",
		es: "pago",
	},
	"checkout/login": {
		nl: "afrekenen/inloggen",
		fr: "paiement/se-connecter",
		de: "kasse/anmelden",
		it: "pagamento/accedi",
		es: "pago/iniciar-sesion",
	},
	"checkout/information": {
		nl: "afrekenen/gegevens",
		fr: "paiement/informations",
		de: "kasse/informationen",
		it: "pagamento/informazioni",
		es: "pago/informacion",
	},
	"checkout/payment": {
		nl: "afrekenen/betaling",
		fr: "paiement/paiement",
		de: "kasse/zahlung",
		it: "pagamento/pagamento",
		es: "pago/pago",
	},
	"p/[slug]": {
		nl: "p/[slug]",
		fr: "p/[slug]",
		de: "p/[slug]",
		it: "p/[slug]",
		es: "p/[slug]",
	},
	login: {
		nl: "inloggen",
		fr: "se-connecter",
		de: "anmelden",
		it: "accedi",
		es: "iniciar-sesion",
	},
	register: {
		nl: "registreren",
		fr: "s-inscrire",
		de: "registrieren",
		it: "iscriversi",
		es: "registrarse",
	},
	"forgot-password": {
		nl: "wachtwoord-vergeten",
		fr: "mot-de-passe-oublie",
		de: "passwort-vergessen",
		it: "password-dimenticato",
		es: "contrasena-olvidada",
	},
	"reset-password": {
		nl: "wachtwoord-herstellen",
		fr: "reinitialiser-le-mot-de-passe",
		de: "passwort-zurucksetzen",
		it: "ripristina-la-password",
		es: "restablecer-la-contrasena",
	},
	"retry-payment": {
		nl: "betaling-opnieuw-proberen",
		fr: "reessayer-le-paiement",
		de: "zahlung-erneut-versuchen",
		it: "pagamento-errore",
		es: "pago-error",
	},
	"order-expired": {
		nl: "bestelling-verlopen",
		fr: "commande-expiree",
		de: "bestellung-abgelaufen",
		it: "ordine-scaduto",
		es: "pedido-caducado",
	},
	confirmation: {
		nl: "bevestiging",
		fr: "confirmation",
		de: "bestatigung",
		it: "conferma",
		es: "confirmacion",
	},
	"customerservice/confirmation": {
		nl: "klantenservice/bevestiging",
		fr: "service-client/confirmation",
		de: "kundenservice/bestatigung",
		it: "servizio-cliente/conferma",
		es: "servicio-al-cliente/confirmacion",
	},
	account: {
		nl: "account",
		fr: "compte",
		de: "konto",
		it: "conto",
		es: "cuenta",
	},
	"account/orders": {
		nl: "account/bestellingen",
		fr: "compte/commandes",
		de: "konto/bestellungen",
		it: "conto/ordini",
		es: "cuenta/pedidos",
	},
	"account/orders/[orderNumber]": {
		nl: "account/bestellingen/[orderNumber]",
		fr: "compte/commandes/[orderNumber]",
		de: "konto/bestellungen/[orderNumber]",
		it: "conto/ordini/[orderNumber]",
		es: "cuenta/pedidos/[orderNumber]",
	},
	"account/orders/[orderNumber]/pay": {
		nl: "account/bestellingen/[orderNumber]/betalen",
		fr: "compte/commandes/[orderNumber]/payer",
		de: "konto/bestellungen/[orderNumber]/zahlung",
		it: "conto/ordini/[orderNumber]/pagamento",
		es: "cuenta/pedidos/[orderNumber]/pago",
	},
	"account/orders/[orderNumber]/confirmation": {
		nl: "account/bestellingen/[orderNumber]/bevestiging",
		fr: "compte/commandes/[orderNumber]/confirmation",
		de: "konto/bestellungen/[orderNumber]/bestatigung",
		it: "conto/ordini/[orderNumber]/conferma",
		es: "cuenta/pedidos/[orderNumber]/confirmacion",
	},
	"account/orders/[orderNumber]/retry-payment": {
		nl: "account/bestellingen/[orderNumber]/betaling-opnieuw-proberen",
		fr: "compte/commandes/[orderNumber]/reessayer-le-paiement",
		de: "konto/bestellungen/[orderNumber]/zahlung-erneut-versuchen",
		it: "conto/ordini/[orderNumber]/pagamento-errore",
		es: "cuenta/pedidos/[orderNumber]/pago-error",
	},
	"account/profile": {
		nl: "account/profiel",
		fr: "compte/profil",
		de: "konto/profil",
		it: "conto/profil",
		es: "cuenta/perfil",
	},
	"account/profile/edit": {
		// edit user name, billing address, preferences
		nl: "account/profiel/bewerken",
		fr: "compte/profil/modifier",
		de: "konto/profil/bearbeiten",
		it: "conto/profil/modifica",
		es: "cuenta/perfil/editar",
	},
	"account/edit": {
		// edit login, password, email
		nl: "account/bewerken",
		fr: "compte/modifier",
		de: "konto/bearbeiten",
		it: "conto/modifica",
		es: "cuenta/editar",
	},
	"account/wishlist": {
		nl: "account/verlanglijst",
		fr: "compte/liste-de-souhaits",
		de: "konto/wunschliste",
		it: "conto/lista-desideri",
		es: "cuenta/lista-de-deseos",
	},
	"account/delete": {
		nl: "account/verwijderen",
		fr: "compte/supprimer",
		de: "konto/loschen",
		it: "conto/cancellare",
		es: "cuenta/eliminar",
	},
	"account-deleted": {
		nl: "account-verwijderd",
		fr: "compte-supprime",
		de: "konto-geloscht",
		it: "conto-cancellato",
		es: "cuenta-eliminada",
	},
	"payment-redirect/[orderNumber]": {
		nl: "payment-redirect/[orderNumber]",
		fr: "payment-redirect/[orderNumber]",
		de: "payment-redirect/[orderNumber]",
		it: "payment-redirect/[orderNumber]",
		es: "payment-redirect/[orderNumber]",
	},
	"leave-a-review": {
		nl: "laat-een-review-achter",
		fr: "laisser-un-avis",
		de: "bewertung-hinterlassen",
		it: "lasciare-un-recensione",
		es: "dejar-una-revisin",
	},
	blog: {
		nl: "blog",
		fr: "blog",
		de: "blog",
		it: "blog",
		es: "blog",
	},
	"compare/[[...skus]]": {
		nl: "vergelijken/[[...skus]]",
		fr: "comparer/[[...skus]]",
		de: "vergleichen/[[...skus]]",
		it: "confrontare/[[...skus]]",
		es: "comparar/[[...skus]]",
	},
	configurator: {
		nl: "configurator",
		fr: "configurateur",
		de: "konfigurator",
		it: "configuratore",
		es: "configurador",
	},
	"product-preview/[id]": {
		nl: "product-preview/[id]",
		fr: "product-preview/[id]",
		de: "product-preview/[id]",
		it: "product-preview/[id]",
		es: "product-preview/[id]",
	},
	"newsletter-unsubscribe": {
		nl: "nieuwsbrief-uitschrijven",
		fr: "désabonnement-newsletter",
		de: "newsletter-abmelden",
		en: "newsletter-unsubscribe",
		it: "annulla-iscrizione-newsletter",
		es: "cancelar-suscripción-newsletter",
	},
};

export type AccountNode = {
	path: PageName;
	children?: AccountNode[];
};

export const accountBreadcrumbHierarchy: AccountNode = {
	path: "account",
	children: [
		{
			path: "account/profile",
			children: [
				{
					path: "account/profile/edit",
				},
				{ path: "account/edit" },
				{
					path: "account/delete",
				},
			],
		},
		{
			path: "account/orders",
			children: [
				{
					path: "account/orders/[orderNumber]",
					children: [
						{ path: "account/orders/[orderNumber]/confirmation" },
						{ path: "account/orders/[orderNumber]/pay" },
						{ path: "account/orders/[orderNumber]/retry-payment" },
					],
				},
			],
		},
		{
			path: "account/wishlist",
		},
	],
};

export const getAccountBreadcrumbs = (
	pageName: PageName,
	node: AccountNode = accountBreadcrumbHierarchy,
	path: AccountNode[] = [],
): AccountNode[] => {
	if (pageName === node.path) {
		return [...path, { path: node.path }];
	}
	if (node.children) {
		for (const child of node.children) {
			const result = getAccountBreadcrumbs(pageName, child, [...path, node]);
			if (result.length) {
				return result;
			}
		}
	}
	return [];
};

/**
 * Returns the untranslated static page name for a given translated static page name.
 */
export const findPageName = (pathname: string, language: Language): PageName =>
	(Object.keys(pageTranslations) as PageName[]).find((name) =>
		matchPageName(
			pathname,
			language === "en" || name === "content/[...slug]"
				? name
				: pageTranslations[name][language],
		),
	) ?? "content/[...slug]";

/**
 * Returns the untranslated static page name for a given translated static page name.
 *
 * @example
 * ```ts
 * matchPageName("p/test", "p/[slug]"); // true
 * matchPageName("s/test", "p/[slug]"); // wrong
 * matchPageName("p/test", "p/wrong/[slug]"); // false
 * matchPageName("vergelijken/1234", "compare/[[...skus]]"); // true
 * matchPageName("vergelijken/1234", "compare/[[skus]]"); // false
 * matchPageName("vergelijken", "compare/[[...skus]]"); // true
 * matchPageName("vergelijken", "compare/[[skus]]"); // false
 * ```
 */
export const matchPageName = (pathname: string, translatedPageName: string) => {
	const pathParts = pathname.split("/");
	const staticPageParts = translatedPageName.split("/");

	const optionalCatchAllIndex = staticPageParts.findIndex(
		(part) => part.startsWith("[[") && part.endsWith("]]"),
	);

	if (optionalCatchAllIndex !== -1) {
		const requiredParts = staticPageParts.slice(0, optionalCatchAllIndex);
		return (
			pathParts.length >= requiredParts.length &&
			requiredParts.every(
				(staticPart, i) =>
					staticPart.startsWith("[") || pathParts[i] === staticPart,
			)
		);
	}

	return (
		pathParts.length === staticPageParts.length &&
		pathParts.every(
			(part, i) =>
				staticPageParts[i].startsWith("[") || part === staticPageParts[i],
		)
	);
};

/**
 *
 * @param pathname E.g. "account/bestellingen/1234"
 * @param translatedPageName E.g. account/bestellingen/[orderNumber]
 * @returns
 */
export const getDynamicParts = (
	pathname: string,
	translatedPageName: string,
) => {
	const pathParts = pathname.split("/");
	const staticPageParts = translatedPageName.split("/");

	return pathParts.reduce(
		(acc, part, i) => {
			const j = Math.min(i, staticPageParts.length - 1);

			if (staticPageParts[j].startsWith("[")) {
				// transform "[slug]" or [...slug] into "slug"
				const key = staticPageParts[j].replace(/\[(\.\.\.)?|\]/g, "");

				// join parts with slashes for catch all routes
				const existing = acc[key];
				if (existing) {
					acc[key] = [acc[key], part].join("/");
				} else {
					acc[key] = part;
				}
			}
			return acc;
		},
		{} as Record<string, string>,
	);
};

export const getStaticUrl = (
	staticPageName: PageName | "home",
	language: Language,
	country: Country,
	params: Record<string, string> = {},
) => {
	const sitePath = getSitePath(country, language);

	if (staticPageName === "home") {
		return sitePath;
	}

	const translatedPageName =
		language === "en" || staticPageName === "content/[...slug]"
			? staticPageName
			: pageTranslations[staticPageName][language];

	return [sitePath, replaceParams(translatedPageName, params)].join("/");
};

export function getProductUrl(
	localizedSlug: LocalizedString,
	site: string,
	language: Language,
	country: Country,
	variant?: ProductVariant,
): string | undefined {
	const staticUrl = getStaticUrl("p/[slug]", language, country);
	const slug = getLocalizedStrict(localizedSlug, language);

	if (slug) {
		return `${site}${replaceParams(staticUrl, { slug })}${variant ? `?sku=${variant.sku}` : ""}`;
	}
	return;
}

/**
 * Replaces dynamic parts denoted by square brackets in a URL with the values from the params object.
 * Handles optional catch-all routes by removing the entire segment if no value is provided.
 *
 * @example
 * ```ts
 * const url = replaceParams("p/[slug]", { slug: "my-product" });
 * // url === "p/my-product"
 *
 * const url2 = replaceParams("compare/[[...skus]]", { skus: "" });
 * // url2 === "compare"
 *
 * const url3 = replaceParams("compare/[[...skus]]", { skus: "sku1/sku2" });
 * // url3 === "compare/sku1/sku2"
 * ```
 */
export const replaceParams = (
	urlTemplate: string,
	params: Record<string, string>,
) =>
	Object.entries(params).reduce((acc, [key, value]) => {
		if (acc.includes(`[[...${key}]]`)) {
			return value
				? acc.replace(`[[...${key}]]`, value)
				: acc.replace(`/[[...${key}]]`, "").replace(`[[...${key}]]`, "");
		}

		return acc.replace(`[${key}]`, value);
	}, urlTemplate);

export const stripSiteName = (pathname: string) =>
	pathname.split("/").slice(2).join("/");

/**
 * Changes /nl/c/compressoren/compressor-1 into compressoren_compressor-1
 */
export const simplifyCategoryPathname = (path: string) =>
	path.split("/").slice(3).join("_");

/**
 * Generates a short 4-character hash from the breadcrumbs string using FNV-1a.
 */
export const generateCrumbHash = (breadcrumbKey: string): string => {
	let hash = 0x811c9dc5; // FNV-1a 32-bit offset basis
	for (let i = 0; i < breadcrumbKey.length; i++) {
		hash ^= breadcrumbKey.charCodeAt(i);
		hash +=
			(hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
	}
	return (hash >>> 0).toString(36).slice(0, 4); // Convert to base36 and take first 4 chars
};

/**
 * Changes `["/nl/c/cat-x", "/nl/c/subcat-y"]` into `"cat-x>subcat-y"` into "9mhl"
 */
export const createCategoryCrumbs = (pathnames: string[]) =>
	pathnames.map(simplifyCategoryPathname).join(">");

export const createCategoryCrumbsHash = (pathnames: string[]) =>
	generateCrumbHash(createCategoryCrumbs(pathnames));

/**
 * Finds the correct breadcrumbs based on either a short hash (new) or a full breadcrumb string (old).
 *
 * @param allBreadcrumbs
 * @param crumb
 */
export const findBreadcrumbs = (
	allBreadcrumbs: Breadcrumb[][],
	crumb?: string,
) => {
	if (!crumb) {
		return allBreadcrumbs[0] ?? [];
	}

	// Strip breadcrumbs from first and last breadcrumb (home & pdp)
	const strippedBreadcrumbs = allBreadcrumbs.map((b) => b.slice(1, -1));

	// Expand breadcrumbs array to include all intermediate levels
	const expandedBreadcrumbs = expandBreadcrumbs(strippedBreadcrumbs);

	// Create a map of hashes to breadcrumb paths (for new short-hash-based URLs)
	const breadcrumbMap = new Map(
		expandedBreadcrumbs.map((breadcrumbs) => {
			const hash = createCategoryCrumbsHash(breadcrumbs.map((b) => b.path));
			const originalIndex = getOriginalIndex(strippedBreadcrumbs, breadcrumbs);
			return [hash, allBreadcrumbs[originalIndex]];
		}),
	);

	// Check if the given `crumb` is a short hash
	if (breadcrumbMap.has(crumb)) {
		return breadcrumbMap.get(crumb) ?? [];
	}

	// TODO: Remove this fallback once we've migrated to the new crumb hash param
	return (
		allBreadcrumbs.find(
			(breadcrumbs) =>
				createCategoryCrumbs(breadcrumbs.slice(1, -1).map((b) => b.path)) ===
				crumb,
		) ?? allBreadcrumbs[0]
	);
};

/**
 * Finds the original index of the breadcrumbs (before expansion).
 *
 * @param strippedBreadcrumbs
 * @param breadcrumbs
 */
export const getOriginalIndex = (
	strippedBreadcrumbs: Breadcrumb[][],
	breadcrumbs: Breadcrumb[],
) => {
	// First, try to find an exact match (all paths match in same order)
	const exactMatch = strippedBreadcrumbs.findIndex(
		(b) =>
			b.length === breadcrumbs.length &&
			breadcrumbs.every((bc, i) => b[i].path === bc.path),
	);

	if (exactMatch !== -1) return exactMatch;

	// If no exact match, find the most specific (longest) matching breadcrumb
	// where all paths in the input breadcrumbs exist in the same order
	let bestMatchIndex = -1;
	let bestMatchScore = -1;

	strippedBreadcrumbs.forEach((breadcrumbArray, index) => {
		// Check if all paths in 'breadcrumbs' exist in 'breadcrumbArray' in the same order
		let allFound = true;
		let lastFoundIndex = -1;
		let score = 0;

		for (const bc of breadcrumbs) {
			const foundIndex = breadcrumbArray.findIndex(
				(b, i) => i > lastFoundIndex && b.path === bc.path,
			);

			if (foundIndex === -1) {
				allFound = false;
				break;
			}

			lastFoundIndex = foundIndex;
			score++; // Increment score for each matching breadcrumb
		}

		// If this is a better match than previous ones, update bestMatchIndex
		if (allFound && score > bestMatchScore) {
			bestMatchScore = score;
			bestMatchIndex = index;
		}
	});

	return bestMatchIndex !== -1 ? bestMatchIndex : 0;
};

/**
 * Expands breadcrumbs array to include all intermediate levels.
 * For example, [A, B, C] becomes [[A], [A, B], [A, B, C]]
 *
 * @param breadcrumbsArray
 */
export const expandBreadcrumbs = (
	breadcrumbsArray: Breadcrumb[][],
): Breadcrumb[][] => {
	const result: Breadcrumb[][] = [];
	const pathSets = new Set<string>(); // Set to track unique path combinations

	breadcrumbsArray.forEach((breadcrumbs) => {
		for (let i = 0; i < breadcrumbs.length; i++) {
			const subBreadcrumbs = breadcrumbs.slice(0, i + 1);
			const pathKey = subBreadcrumbs.map((b) => b.path).join("|");

			// Only add if this exact combination of paths doesn't exist already
			if (!pathSets.has(pathKey)) {
				pathSets.add(pathKey);
				result.push(subBreadcrumbs);
			}
		}
	});

	return result;
};

/**
 * Returns the Google Maps URL for a given address and locale, which is used for the "Route" button in the pickup info section.
 * @param locale
 * @param address
 */
export const getGoogleMapsUrl = (
	locale: string,
	address: {
		streetName: string;
		streetNumber: string;
		postalCode: string;
		city: string;
	},
): string => {
	// Format the address string
	const formattedAddress = `HBM Machines, ${address.streetName} ${address.streetNumber}, ${address.postalCode} ${address.city}`;

	// Encode the formatted address to ensure it's URL-safe
	const encodedAddress = encodeURIComponent(formattedAddress);

	// Return the Google Maps URL based on the locale
	switch (locale) {
		case "nl-NL":
			return `https://www.google.nl/maps/search/?api=1&query=${encodedAddress}`;
		case "en-GB":
			return `https://www.google.co.uk/maps/search/?api=1&query=${encodedAddress}`;
		case "fr-FR":
			return `https://www.google.fr/maps/search/?api=1&query=${encodedAddress}`;
		case "de-DE":
			return `https://www.google.de/maps/search/?api=1&query=${encodedAddress}`;
		case "nl-BE":
		case "fr-BE":
			return `https://www.google.be/maps/search/?api=1&query=${encodedAddress}`;
		default:
			// Default to Google Maps global URL for unsupported locales
			return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
	}
};

/**
 * Returns the Klarna FAQ URL for a given locale and country,
 * which is used for the "Klarna FAQ" text in the confirmation page / email.
 * @param locale
 * @param country
 */
export const getKlarnaFaqUrl = (locale: string, country: string): string => {
	const language = languageOf(locale);
	switch (language) {
		case "nl":
			return country === "BE"
				? "https://www.klarna.com/be/klantenservice/"
				: "https://www.klarna.com/nl/klantenservice/";
		case "de":
			return country === "AT"
				? "https://www.klarna.com/at/kundenservice/"
				: "https://www.klarna.com/de/kundenservice/";
		case "fr":
			return country === "BE"
				? "https://www.klarna.com/be/fr/service-client/"
				: "https://www.klarna.com/fr/service-client/";
		default:
			return "https://www.klarna.com/customer-service/";
	}
};

/**
 * Add search params to an URL that may already contain search params
 *
 * @param url
 * @param searchParams
 */
export const addSearchParams = (
	url: string,
	searchParams: URLSearchParams,
): string => {
	const urlObj = new URL(
		url,
		!url.startsWith("http") ? "http://localhost" : undefined,
	);

	searchParams.forEach((value, key) => urlObj.searchParams.append(key, value));

	return urlObj.toString().replace("http://localhost", "");
};
